import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Typography, Spin, App } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const Login: React.FC = () => {
  const { message } = App.useApp(); // 使用 App 的 hook 获取 message
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  // 检查系统是否已初始化
  useEffect(() => {
    const checkInit = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/check-init`);
        if (response.data.requireSetup) {
          // 需要初始化，跳转到初始化页面
          navigate('/init-setup');
        }
      } catch (error) {
        console.error('检查初始化状态失败:', error);
      } finally {
        setChecking(false);
      }
    };

    checkInit();
  }, [navigate]);

  const onFinish = async (values: {
    username: string;
    password: string;
    totpCode?: string;
  }) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username: values.username,
        password: values.password,
        totpCode: values.totpCode || undefined, // 如果为空则不传
      });

      const { token, admin } = response.data;

      // 保存 Token 到 localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('admin', JSON.stringify(admin));

      message.success('登录成功！');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('[登录失败]', error);

      // 详细的错误处理
      let errorMessage = '登录失败';

      if (error.response) {
        // 服务器返回了错误响应
        console.log('[登录失败] 状态码:', error.response.status);
        console.log('[登录失败] 错误数据:', error.response.data);

        if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.status === 401) {
          errorMessage = '用户名或密码错误';
        } else if (error.response.status === 400) {
          errorMessage = '请求参数错误';
        } else {
          errorMessage = `登录失败 (${error.response.status})`;
        }
      } else if (error.request) {
        // 请求已发送但没有收到响应
        console.error('[登录失败] 无响应:', error.request);
        errorMessage = '网络连接失败，请检查服务器状态';
      } else {
        // 设置请求时发生错误
        console.error('[登录失败] 请求错误:', error.message);
        errorMessage = error.message || '登录失败';
      }

      console.log('[显示错误消息]', errorMessage);
      message.error(errorMessage, 5); // 显示 5 秒
    } finally {
      setLoading(false);
    }
  };

  // 显示加载状态
  if (checking) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Spin size="large" tip="正在加载..." />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Card
        style={{
          maxWidth: 450,
          width: '100%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            TG 机器人管理后台
          </Title>
          <Text type="secondary">安全登录</Text>
        </div>

        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
            />
          </Form.Item>

          <Form.Item
            name="totpCode"
            rules={[
              { pattern: /^\d{6}$/, message: '验证码必须是 6 位数字' },
            ]}
            extra="如果已绑定 Google Authenticator，请输入验证码"
          >
            <Input
              prefix={<SafetyOutlined />}
              placeholder="6 位验证码（可选）"
              maxLength={6}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              登录
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            🔒 支持 Google 2FA 双因素认证（可选）
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;
